import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import CartService from '../services/cartService';
import type { ShippingAddress, PaymentDetails } from '../services/cartService';

// Define the cart item interface with strict typing
export interface CartItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
  image: string;
  unit: string;
  seller: {
    name: string;
    location: string;
  };
}

// Define the cart context interface
interface CartContextType {
  cartItems: CartItem[];
  loading: boolean;
  addToCart: (product: Omit<CartItem, 'quantity'>, quantity?: number) => Promise<void>;
  removeFromCart: (productId: string) => Promise<void>;
  updateQuantity: (productId: string, quantity: number) => Promise<void>;
  clearCart: () => Promise<void>;
  getCartTotal: () => number;
  getItemCount: () => number;
  isInCart: (productId: string) => boolean;
  checkout: (shippingAddress: ShippingAddress, paymentDetails: PaymentDetails) => Promise<{ success: boolean; orderId?: string; error?: string }>;
  refreshCart: () => Promise<void>;
}

// Create the cart context with default values
const CartContext = createContext<CartContextType>({
  cartItems: [],
  loading: false,
  addToCart: async () => {},
  removeFromCart: async () => {},
  updateQuantity: async () => {},
  clearCart: async () => {},
  getCartTotal: () => 0,
  getItemCount: () => 0,
  isInCart: () => false,
  checkout: async () => ({ success: false, error: 'Not implemented' }),
  refreshCart: async () => {},
});

// Custom hook to use the cart context
export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

// Cart provider component
export const CartProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [initialized, setInitialized] = useState(false);

  // Initialize cart on component mount and auth state change
  useEffect(() => {
    const initializeCart = async () => {
      setLoading(true);
      try {
        if (isAuthenticated && user?.id) {
          // Load cart from database for authenticated users
          const dbCartItems = await CartService.loadCartFromDatabase(user.id);
          
          // Merge with localStorage cart if exists
          const localCart = getLocalStorageCart();
          if (localCart.length > 0) {
            // Merge local cart with database cart
            const mergedCart = mergeCartItems([...dbCartItems, ...localCart]);
            setCartItems(mergedCart);
            
            // Save merged cart to database
            await CartService.saveCartToDatabase(user.id, mergedCart);
            
            // Clear localStorage after merging
            clearLocalStorageCart();
          } else {
            setCartItems(dbCartItems);
          }
        } else {
          // Load from localStorage for non-authenticated users
          const localCart = getLocalStorageCart();
          setCartItems(localCart);
        }
      } catch (error) {
        console.error('Error initializing cart:', error);
        // Fallback to localStorage
        const localCart = getLocalStorageCart();
        setCartItems(localCart);
      } finally {
        setLoading(false);
        setInitialized(true);
      }
    };

    if (!initialized) {
      initializeCart();
    }
  }, [isAuthenticated, user?.id, initialized]);

  // Save cart items to appropriate storage whenever they change
  useEffect(() => {
    if (!initialized) return;

    const saveCart = async () => {
      try {
        if (isAuthenticated && user?.id) {
          // Save to database for authenticated users
          await CartService.saveCartToDatabase(user.id, cartItems);
        } else {
          // Save to localStorage for non-authenticated users
          saveToLocalStorage(cartItems);
        }
      } catch (error) {
        console.error('Error saving cart:', error);
        // Fallback to localStorage
        saveToLocalStorage(cartItems);
      }
    };

    saveCart();
  }, [cartItems, isAuthenticated, user?.id, initialized]);

  // Helper functions for localStorage operations
  const getLocalStorageCart = (): CartItem[] => {
    if (typeof window !== 'undefined') {
      try {
        const savedCart = localStorage.getItem('farmers_hub_cart');
        if (!savedCart) return [];
        
        const parsedCart = JSON.parse(savedCart);
        
        // Filter out invalid items (e.g. legacy items with number IDs)
        if (Array.isArray(parsedCart)) {
          return parsedCart.filter(item => 
            item && 
            typeof item.id === 'string' && 
            item.id.length > 0 && 
            // Check if it's a valid UUID (simple check) or at least not a simple number string
            // Actually, for now, just ensure it is a string. If it's "1", it might still fail if passed to UUID column.
            // Let's rely on the fact that new items are UUIDs.
            // If we encounter "1", it's legacy.
            isNaN(Number(item.id)) 
          );
        }
        return [];
      } catch (error) {
        console.error('Error reading cart from localStorage:', error);
        return [];
      }
    }
    return [];
  };

  const saveToLocalStorage = (items: CartItem[]) => {
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem('farmers_hub_cart', JSON.stringify(items));
      } catch (error) {
        console.error('Error saving cart to localStorage:', error);
      }
    }
  };

  const clearLocalStorageCart = () => {
    if (typeof window !== 'undefined') {
      try {
        localStorage.removeItem('farmers_hub_cart');
      } catch (error) {
        console.error('Error clearing cart from localStorage:', error);
      }
    }
  };

  // Merge cart items, combining quantities for duplicate products
  const mergeCartItems = (items: CartItem[]): CartItem[] => {
    const merged: { [key: string]: CartItem } = {};
    
    items.forEach(item => {
      if (merged[item.id]) {
        merged[item.id].quantity += item.quantity;
      } else {
        merged[item.id] = { ...item };
      }
    });
    
    return Object.values(merged);
  };

  // Add a product to the cart
  const addToCart = async (product: any, quantity: number = 1) => {
    setLoading(true);
    try {
      setCartItems(prevItems => {
        // Ensure ID is string
        const productId = product.id.toString();
        const existingItem = prevItems.find(item => item.id === productId);

        if (existingItem) {
          return prevItems.map(item =>
            item.id === productId
              ? { ...item, quantity: item.quantity + quantity }
              : item
          );
        }

        return [...prevItems, {
          id: productId,
          name: product.name,
          price: product.price,
          quantity: quantity,
          image: product.image,
          unit: product.unit,
          seller: {
            name: product.seller.name,
            location: product.seller.location,
          },
        }];
      });
    } catch (error) {
      console.error('Error adding to cart:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Remove a product from the cart
  const removeFromCart = async (productId: string) => {
    setLoading(true);
    try {
      setCartItems(prevItems => prevItems.filter(item => item.id !== productId));
    } catch (error) {
      console.error('Error removing from cart:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Update the quantity of a product in the cart
  const updateQuantity = async (productId: string, quantity: number) => {
    setLoading(true);
    try {
      if (quantity <= 0) {
        await removeFromCart(productId);
        return;
      }

      setCartItems(prevItems =>
        prevItems.map(item =>
          item.id === productId
            ? { ...item, quantity: quantity }
            : item
        )
      );
    } catch (error) {
      console.error('Error updating quantity:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Clear the cart
  const clearCart = async () => {
    setLoading(true);
    try {
      setCartItems([]);
      if (isAuthenticated && user?.id) {
        await CartService.clearCartFromDatabase(user.id);
      } else {
        clearLocalStorageCart();
      }
    } catch (error) {
      console.error('Error clearing cart:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Checkout process
  const checkout = async (shippingAddress: ShippingAddress, paymentDetails: PaymentDetails) => {
    if (!isAuthenticated || !user?.id) {
      return { success: false, error: 'Please log in to complete your order' };
    }

    if (cartItems.length === 0) {
      return { success: false, error: 'Your cart is empty' };
    }

    setLoading(true);
    try {
      const order = await CartService.createOrder(user.id, cartItems, shippingAddress, paymentDetails);
      
      // Clear cart after successful order
      setCartItems([]);
      
      return { success: true, orderId: order.id };
    } catch (error) {
      console.error('Checkout error:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Checkout failed. Please try again.' 
      };
    } finally {
      setLoading(false);
    }
  };

  // Refresh cart from database
  const refreshCart = async () => {
    if (!isAuthenticated || !user?.id) return;
    
    setLoading(true);
    try {
      const dbCartItems = await CartService.loadCartFromDatabase(user.id);
      setCartItems(dbCartItems);
    } catch (error) {
      console.error('Error refreshing cart:', error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate the total price of all items in the cart
  const getCartTotal = () => {
    return cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  // Get the total number of items in the cart
  const getItemCount = () => {
    return cartItems.reduce((count, item) => count + item.quantity, 0);
  };

  // Check if a product is in the cart
  const isInCart = (productId: string) => {
    return cartItems.some(item => item.id === productId);
  };

  return (
    <CartContext.Provider value={{
       cartItems,
       loading,
       addToCart,
       removeFromCart,
       updateQuantity,
       clearCart,
       getCartTotal,
       getItemCount,
       isInCart,
       checkout,
       refreshCart
     }}>
      {children}
    </CartContext.Provider>
  );
};